import { ReactNode } from 'react';
import { GetProposalData } from '../../../api/gms/proposal/proposal-api-state';
import { proposalHeaderData, STATUS_STYLES } from '../../../constant/gms-constant';
import { ProposalIconType } from '../../../types/type';
import TableEmpty from '../../ui/table-empty';
import TableError from '../../ui/table-error';
import TableLoading from '../../ui/table-loading';

export const getStatusStyle = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    return STATUS_STYLES[normalizedStatus] ?? 'border-gray-200 bg-gray-50 text-gray-600';
};

interface Props {
    typeOfIcon: ProposalIconType;
    isLoadingGetProposal: boolean;
    isErrorGetProposal: boolean;
    errorMessage: string;
    spinnerColor?: string;
    spinnerSize?: number;
    getProposal: GetProposalData[] | undefined;
    handleEdit?: (value: number, userID: number, status: string) => void;
    handleGenerate?: (value: number, userID: number, status: string) => void;
    headers?: string[];
    disableRowClick?: boolean;
}

export default function ProposalListTBody({
    headers = [],
    typeOfIcon,
    isLoadingGetProposal,
    isErrorGetProposal,
    errorMessage,
    spinnerColor = '#186cb2',
    spinnerSize = 25,
    getProposal,
    handleEdit,
    handleGenerate,
    disableRowClick = false,
}: Props) {
    
    const renderColumn = (header: string, proposal: GetProposalData, status: string, ebamStatus: string, actuarialStatus: string): ReactNode => {
        switch (header) {
            case proposalHeaderData.computedBy.label:
                return (
                    <span
                        className="truncate"
                        title={proposal.actuarial_approval?.endorsed_by_name ?? '-'}
                    >
                        {proposal.actuarial_approval?.endorsed_by_name ?? '-'}
                    </span>
                );

            case proposalHeaderData.proposalStatus.label:
                return (
                    <span
                        className={`inline-flex truncate rounded-full border px-2.5 py-1 text-[10px] font-semibold capitalize sm:text-[11px] ${getStatusStyle(status)}`}
                        title={status}
                    >
                        {status}
                    </span>
                );

            case proposalHeaderData.ratesStatus.label:
                return (
                    <span
                        className={`inline-flex truncate rounded-full border px-2.5 py-1 text-[10px] font-semibold capitalize sm:text-[11px] ${getStatusStyle(actuarialStatus)}`}
                        title={actuarialStatus}
                    >
                        {actuarialStatus}
                    </span>
                );

            case proposalHeaderData.policyStatus.label:
                return (
                    <span
                        className={`inline-flex truncate rounded-full border px-2.5 py-1 text-[10px] font-semibold capitalize sm:text-[11px] ${getStatusStyle(ebamStatus)}`}
                        title={ebamStatus}
                    >
                        {ebamStatus}
                    </span>
                );

            case proposalHeaderData.proposalAs.label:
                return (
                    <div
                        className="truncate"
                        title={proposal?.proposal_type?.name ?? '-'}
                    >
                        {proposal?.proposal_type?.name ?? '-'}
                    </div>
                );

            case proposalHeaderData.effectiveDate.label:
                return (
                    <div
                        className="truncate font-medium text-gray-800"
                        title={proposal.effective_date?.split('T')[0] ?? '-'}
                    >
                        {proposal.effective_date?.split('T')[0] ?? '-'}
                    </div>
                );

            case proposalHeaderData.groupName.label:
                return (
                    <div
                        className="truncate font-medium text-gray-800"
                        title={proposal.group_name ?? '-'}
                    >
                        {proposal.group_name ?? '-'}
                    </div>
                );

            case proposalHeaderData.email.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal.email ?? '-'}
                    >
                        {proposal.email ?? '-'}
                    </div>
                );

            case proposalHeaderData.proposalType.label:
                return (
                    <div
                        className="truncate text-gray-700"
                        title={proposal?.type_of_proposal?.name ?? '-'}
                    >
                        {proposal?.type_of_proposal?.name ?? '-'}
                    </div>
                );

            case proposalHeaderData.product.label:
                return (
                    <div
                        className="truncate text-gray-700"
                        title={proposal?.plan?.name || proposal?.prototype_plan?.name || '-'}
                    >
                        {proposal?.plan?.name || proposal?.prototype_plan?.name || '-'}
                    </div>
                );

            case proposalHeaderData.preparedBy.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal.user_full_name ?? '-'}
                    >
                        {proposal.user_full_name ?? '-'}
                    </div>
                );
            
            case proposalHeaderData.renewedBy.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={'-'}
                    >
                        {'-'}
                    </div>
                );

            case proposalHeaderData.ratesBy.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal.actuarial_approval?.endorsed_by_name || '-'}
                    >
                        {proposal.actuarial_approval?.endorsed_by_name || '-'}
                    </div>
                );

            case proposalHeaderData.endorsedBy.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal.actuarial_approval?.endorsed_by_name || '-'}
                    >
                        {proposal.actuarial_approval?.endorsed_by_name || '-'}
                    </div>
                );

            

            case proposalHeaderData.endorsedTo.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal.actuarial_approval?.endorsed_to_name || '-'}
                    >
                        {proposal.actuarial_approval?.endorsed_to_name || '-'}
                    </div>
                );
            case proposalHeaderData.cocAction.label:
                return (
                    <span
                        onClick={() => handleGenerate?.(proposal.application_id, proposal.user_id, status)}
                        // className={`cursor-pointer inline-flex truncate rounded-full border px-2.5 py-1 text-[10px] font-semibold capitalize sm:text-[11px] border-gray-200 bg-gray-50 text-gray-600`}
                        className={`cursor-pointer inline-flex truncate rounded-xl border border-[#186cb2] text-[#186cb2] px-2.5 py-1 text-[10px] font-semibold capitalize sm:text-[11px] bg-gray-50`}
                        title={`Generate COC`}
                    >
                        <svg className="h-3.5 w-3.5 shrink-0 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path d="M14 2v6h6" />
                            <path d="M8 13h8" />
                            <path d="M8 17h5" />
                        </svg>
                        {`Generate COC`}
                    </span>
                );
            
            case proposalHeaderData.policyNumber.label:
                return (
                    <div
                        className="truncate font-medium text-gray-800"
                        title={proposal.policy_no ?? '-'}
                    >
                        {proposal.policy_no ?? '-'}
                    </div>
                );
            default:
                return '-';
        }
    };

    const getCellClassName = (header: string) => {
        if (header === proposalHeaderData.action.label) {
            return 'px-3 py-3 text-center sm:px-4';
        }

        if (
            header === proposalHeaderData.proposalStatus.label ||
            header === proposalHeaderData.ratesStatus.label ||
            header === proposalHeaderData.policyStatus.label
        ) {
            return 'px-3 py-1 sm:px-4';
        }

        return 'px-3 py-3 text-gray-700 sm:px-4';
    };

    return (
        <tbody className="divide-y divide-gray-100">
            {isLoadingGetProposal ? (
                <TableLoading />
            ) : isErrorGetProposal ? (
                <TableError description={errorMessage} />
            ) : !getProposal || getProposal.length === 0 ? (
                <TableEmpty />
            ) : (
                getProposal.map((proposal, index) => {
                    const status = proposal?.status?.name ?? '-';
                    const ebamStatus = proposal?.ebam_status?.[0]?.status_name ?? '-';
                    const actuarialStatus = proposal?.actuarial_status?.[0]?.status_name ?? '-';

                    return (
                        <tr
                            key={proposal.application_id}
                            onClick={() => !disableRowClick ? handleEdit?.(proposal.application_id, proposal.user_id, status) : undefined}
                            // className={`group cursor-pointer border-b border-gray-100 text-xs transition-colors duration-150 hover:bg-[#186cb2]/5 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                            className={`group border-b border-gray-100 text-xs transition-colors duration-150 ${!disableRowClick ? 'cursor-pointer hover:bg-[#186cb2]/5' : ''} ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                        >
                            {headers.map((header) => (
                                <td
                                    key={header}
                                    className={getCellClassName(header)}
                                    onClick={
                                        header === proposalHeaderData.action.label ? (event) => event.stopPropagation() : undefined}
                                >
                                    {renderColumn(header, proposal, status, ebamStatus, actuarialStatus)}
                                </td>
                            ))}
                        </tr>
                    );
                })
            )}
        </tbody>
    );
}